import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryFunctionContext,
} from '@tanstack/react-query';

import posthog from '@/config/analytics/posthog';
import { logoutMsal } from '@/config/auth/msal';
import { clearSentryUser } from '@/config/monitoring/sentry';
import { ServiceUnavailableError } from '@/utils/errors';
import { navigateTo } from '@/utils/navigationRef';
import { ROUTES } from '@/utils/routeUtils';

import { queryKeys } from './queryKeys';

/**
 * Default query function options
 */
export interface DefaultQueryFnOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  url?: string;
}

/**
 * Default query function for all queries
 * Handles API calls with proper error handling and response parsing
 *
 * @example
 * // Use in a query:
 * useQuery({
 *   queryKey: ['users', userId],
 *   queryFn: (context) => defaultQueryFn(context, { url: `/api/users/${userId}` })
 * })
 */
export async function defaultQueryFn<T = unknown>(
  context: QueryFunctionContext,
  options?: DefaultQueryFnOptions,
): Promise<T> {
  const { queryKey, signal } = context;

  // If options.url is provided, use it; otherwise construct from queryKey
  const url = options?.url ?? (Array.isArray(queryKey) ? queryKey.join('/') : String(queryKey));

  // Ensure URL starts with http or is a relative path
  const apiUrl = url.startsWith('http') ? url : `/api/${url.replace(/^\//, '')}`;

  const method = options?.method ?? 'GET';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  const fetchOptions: RequestInit = {
    headers,
    method,
    signal,
  };

  if (method !== 'GET' && options?.body) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  const response = await fetch(apiUrl, fetchOptions);

  if (!response.ok) {
    const error = new Error(`API Error: ${response.status} ${response.statusText}`);
    // Attach response details for error handling
    (error as Error & { status?: number; statusText?: string }).status = response.status;
    (error as Error & { status?: number; statusText?: string }).statusText = response.statusText;
    throw error;
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return response.text() as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Wrapper function to use defaultQueryFn as the default query function
 * This allows defaultQueryFn to be used automatically for all queries
 * while still allowing custom query functions to be used when needed
 */
function defaultQueryFnWrapper(context: QueryFunctionContext) {
  return defaultQueryFn(context);
}

/** Prevents concurrent logout flows from racing each other. */
let isLoggingOut = false;

/**
 * Performs client-side logout when a ServiceUnavailableError is encountered.
 * Skips the backend logout call because the error means no deployment is configured.
 * A module-level guard ensures the flow only runs once even if multiple queries/mutations
 * fail simultaneously.
 */
async function handleServiceUnavailableLogout(): Promise<void> {
  if (isLoggingOut) {
    return;
  }
  isLoggingOut = true;
  try {
    await logoutMsal();
    clearSentryUser();
    // See useSignOut: clears the identity and the registered `user_email`.
    posthog.reset();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    queryClient.removeQueries({ queryKey: queryKeys.auth.all });
    navigateTo(ROUTES.LOGIN, { replace: true });
  } finally {
    isLoggingOut = false;
  }
}

/**
 * Default query options following best practices:
 * - staleTime: 5 minutes - data is considered fresh for 5 minutes
 * - gcTime: 10 minutes - unused data is garbage collected after 10 minutes
 * - retry: 3 times with exponential backoff, skipped for ServiceUnavailableError
 * - refetchOnWindowFocus: false - don't refetch when window regains focus (good for add-ins)
 * - refetchOnReconnect: true - refetch when network reconnects
 * - queryFn: defaultQueryFn - automatically used for all queries unless overridden
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      networkMode: 'online',
      retry: 1,
    },
    queries: {
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      queryFn: defaultQueryFnWrapper,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: false, // Important for Office add-ins
      retry: (failureCount, error) => {
        // Never retry when the deployment is not configured — it will never succeed
        if (error instanceof ServiceUnavailableError) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000), // Exponential backoff
      staleTime: 1000 * 60 * 5, // 5 minutes
      // Network mode: prefer online, but allow offline queries
      networkMode: 'online',
    },
  },
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error instanceof ServiceUnavailableError) {
        console.error('[QueryClient] ServiceUnavailableError in mutation — logging out');
        handleServiceUnavailableLogout().catch((error) =>
          console.error('[QueryClient] logout failed after mutation error', error),
        );
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ServiceUnavailableError) {
        console.error('[QueryClient] ServiceUnavailableError in query — logging out');
        handleServiceUnavailableLogout().catch((error) =>
          console.error('[QueryClient] logout failed after query error', error),
        );
      }
    },
  }),
});
